import { Box } from "@mui/material";
import Editor, { OnMount } from "@monaco-editor/react";
import * as MonacoTypes from "monaco-editor";
import { useEffect, useRef, useState } from "react";

interface SandboxEditorProps {
    value: string;
    onChange: (value: string) => void;
    onSave?: () => void; // called on Ctrl+S
}

const languageId = "sandboxdsl";

export default function SandboxEditor(props: SandboxEditorProps) {
    const { value, onChange, onSave } = props;
    const [changeTimeout, setChangeTimeout] = useState<NodeJS.Timeout | undefined>(undefined);

    const onSaveRef = useRef<typeof onSave>(onSave);
        useEffect(() => {
            onSaveRef.current = onSave;
        }, [onSave]);

    const initChange = (val) => {
        if(changeTimeout)
            clearTimeout(changeTimeout);
        setChangeTimeout(setTimeout(() => onChange(val ?? ""), 500));
    }

    const handleEditorMount: OnMount = (editor, monaco) => {
        // 1) Register the custom language
        monaco.languages.register({ id: languageId });

        // 2) Define Monarch tokenizer using Monaco types
        const monarchLanguage: MonacoTypes.languages.IMonarchLanguage = {
            defaultToken: "",
            tokenPostfix: ".sandbox",

            keywords: [
                "CONSTS",
                "PHASE",
                "EXTENDS",
                "SET_GLOBAL_DATETIME",
                "CREATE_STATION",
                "CREATE_FACILITY",
                "CREATE_DEVICE",
                "CREATE_READER",
                "CREATE_SIGHTING",
                "PROCESS_SIGHTINGS",
                "GET_VISIT",
                "GET_LATEST_SIGHTING",
                "GET_LATEST_SIGHTING_NON_CHRONOLOGICAL",
                "SET_VARIABLE",
                "ASSERT_EQUAL",
                "ASSERT_NOT_NULL",
                "ASSERT_NULL",
                "ASSERT_CONTAINS_KEY"
            ],

            brackets: [
                { open: "{", close: "}", token: "delimiter.curly" },
                { open: "[", close: "]", token: "delimiter.bracket" },
                { open: "(", close: ")", token: "delimiter.parenthesis" }
            ],

            tokenizer: {
                root: [
                    // Full-line comments starting with #
                    [/\.*?^#.*$/, "comment"],

                    // PHASE Something
                    [/^(PHASE)(\s+.+$)/, ["keyword", "type.identifier"]],

                    // EXTENDS TC-xx
                    [/^(EXTENDS)(\s+.+$)/, ["keyword", "type.identifier"]],

                    // CONSTS block
                    [/\bCONSTS\b/, "keyword"],

                    // Known DSL commands
                    [
                        /\b(SET_GLOBAL_DATETIME|CREATE_STATION|CREATE_FACILITY|CREATE_DEVICE|CREATE_READER|CREATE_SIGHTING|PROCESS_SIGHTINGS|PROCESS_REVISITS|GET_VISIT|GET_READER|GET_LATEST_SIGHTING|GET_LATEST_SIGHTING_NON_CHRONOLOGICAL|SET_VARIABLE|SET_PROPERTY|ASSERT_TRUE|ASSERT_FALSE|ASSERT_EQUAL|ASSERT_NOT_EQUAL|ASSERT_NOT_NULL|ASSERT_NULL|ASSERT_CONTAINS_KEY)\b/,
                        "keyword"
                    ],

                    // Variables like ${Visit.Id}
                    [/\$\{[^}]+\}/, "predefined"],

                    // Strings
                    [/".*?"/, "string"],

                    // ISO timestamps
                    [/\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z\b/, "number"],

                    // Numeric literals
                    [/-?\d+(\.\d+)?/, "number"],

                    // Constant names at start of line: [whitespace] NAME =
                    // All characters of the match are covered by 3 groups:
                    //   1: leading whitespace
                    //   2: NAME
                    //   3: " =" including optional space
                    [/^([\t ]*)([A-Z_][A-Za-z0-9_]*)(\s*=)/, ["white", "constant", "delimiter"]]
                ]
            }
        };

        monaco.languages.setMonarchTokensProvider(languageId, monarchLanguage);

        // 3) Define a light UI theme
        monaco.editor.defineTheme("sandbox-light", {
            base: "vs",
            inherit: true,
            rules: [
                { token: "comment", foreground: "6A9955" },
                { token: "keyword", foreground: "0000FF", fontStyle: "bold" },
                { token: "type.identifier", foreground: "795E26", fontStyle: "bold" },
                { token: "constant", foreground: "800000" },
                { token: "predefined", foreground: "267F99" },
                { token: "string", foreground: "A31515" },
                { token: "number", foreground: "098658" }
            ],
            colors: {
                "editor.background": "#FFFFFF"
            }
        });

        monaco.editor.setTheme("sandbox-light");

        // 4) Ctrl+S → onSave
        editor.addCommand(
            monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
            () => onSaveRef.current?.()
        );
    };

    return (
        <Box
            sx={{
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden"
            }}
        >
            <Editor
                height="60vh"
                language={languageId}
                theme="sandbox-light"
                value={value}
                onMount={handleEditorMount}
                onChange={initChange}
                options={{
                    fontSize: 14,
                    minimap: { enabled: false },
                    automaticLayout: true,
                    scrollBeyondLastLine: false,
                    wordWrap: "on"
                }}
            />
        </Box>
    );
}
